"use client";
import { ChoicePills } from "@/components/marketing";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { useState } from "react";
import type { CompareRow } from "../../data";

interface CompareTableProps {
  choiceLabel: string;
  tableLabel: string;
  rowHeading: string;
  columns: string[];
  rows: CompareRow[];
}

// "Side by side": the five industries in one table; choosing a column
// highlights it.
const CompareTable = ({
  choiceLabel,
  tableLabel,
  rowHeading,
  columns,
  rows,
}: CompareTableProps) => {
  const initialState = { column: "0" };
  const [state, setState] = useState(initialState);
  const options = columns.map((label, index) => ({
    key: String(index),
    label,
  }));
  const setColumn = (column: string) =>
    setState((state) => ({ ...state, column }));
  const isActive = (index: number) => String(index) === state.column;

  return (
    <div>
      <ChoicePills
        ariaLabel={choiceLabel}
        options={options}
        value={state.column}
        onChange={setColumn}
      />
      <div
        tabIndex={0}
        role="region"
        aria-label={tableLabel}
        className="mt-3 overflow-x-auto rounded-3xl border border-line bg-white p-2"
      >
        <table className="w-full min-w-[720px] border-separate border-spacing-y-2 text-[14.5px]">
          <thead>
            <tr>
              <th
                scope="col"
                className="px-3.5 py-3 text-left text-[11.5px] font-bold tracking-[0.07em] text-ink-muted uppercase"
              >
                {rowHeading}
              </th>
              {columns.map((column, index) => (
                <th
                  key={column}
                  scope="col"
                  className={cn(
                    "px-3.5 py-3 text-left text-[11.5px] font-bold tracking-[0.07em] uppercase",
                    isActive(index) ? "text-primary" : "text-ink-muted",
                  )}
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.industry}>
                <th
                  scope="row"
                  className="rounded-l-2xl bg-paper px-3.5 py-3 text-left align-top font-bold"
                >
                  <Link
                    href={row.route}
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    {row.industry}
                  </Link>
                </th>
                {row.values.map((value, index) => (
                  <td
                    key={columns[index]}
                    className={cn(
                      "px-3.5 py-3 text-left align-top transition-colors last:rounded-r-2xl motion-reduce:transition-none",
                      isActive(index)
                        ? "bg-tone-blush font-bold text-ink"
                        : "bg-paper text-ink-muted",
                    )}
                  >
                    {value}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompareTable;
